import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '@/store/store';
import { addLikedVideo, removeLikedVideo } from '@/features/videos/videosSlice';
import { Heart } from 'lucide-react';
import HeartbeatAnimation from './HeartbeatAnimation';
import { toggleLike } from '@/services/likes.api';

interface LikeButtonProps {
  videoId: string;
  videoDbId?: string;
  initialLiked?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'minimal' | 'outline';
  disabled?: boolean;
  className?: string;
  onLikeChange?: (liked: boolean) => void;
}

const LikeButton: React.FC<LikeButtonProps> = ({
  videoId,
  videoDbId,
  initialLiked = false,
  size = 'medium',
  variant = 'default',
  disabled = false,
  className = '',
  onLikeChange
}) => {
  const dispatch = useAppDispatch();
  const [liked, setLiked] = useState(initialLiked);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setLiked(initialLiked);
  }, [initialLiked, videoId]);

  const sizeConfig = {
    small: {
      button: 'p-1.5',
      icon: 'w-4 h-4',
      text: 'text-xs',
      gap: 'gap-1'
    },
    medium: {
      button: 'p-2',
      icon: 'w-5 h-5',  
      text: 'text-sm',
      gap: 'gap-1.5'
    },
    large: {
      button: 'p-3',
      icon: 'w-6 h-6',
      text: 'text-base',
      gap: 'gap-2'
    }
  };

  const variantStyles = {
    default: liked 
      ? 'bg-danger-500 hover:bg-danger-600 text-white' 
      : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-600 hover:text-danger-500',
    minimal: liked
      ? 'text-danger-500 hover:text-danger-600'
      : 'text-neutral-500 hover:text-danger-500',
    outline: liked
      ? 'border-2 border-danger-500 text-danger-500 bg-danger-50 hover:bg-danger-100'
      : 'border-2 border-neutral-300 text-neutral-600 hover:border-danger-400 hover:text-danger-500'
  };

  const handleLikeToggle = async () => {
    if (disabled || isLoading) return;

    const wasLiked = liked;
    const newLiked = !wasLiked;

    setLiked(newLiked);
    if (videoDbId) {
      dispatch(newLiked ? addLikedVideo(videoDbId) : removeLikedVideo(videoDbId));
    }
    setIsLoading(true);

    if (newLiked) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 600);
    }

    try {
      const response = await toggleLike(videoId);

      setLiked(response.liked);

      onLikeChange?.(response.liked);
    } catch (error) {
      setLiked(wasLiked);

      console.error('Like toggle error:', error);

      setIsAnimating(false);
    } finally {
      setIsLoading(false);
    }
  };

  const config = sizeConfig[size];

  return (
    <button
      onClick={handleLikeToggle}
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center
        ${config.button}
        ${config.gap}
        rounded-full
        transition-all duration-200 ease-in-out
        transform hover:scale-105 active:scale-95
        focus:outline-none focus:ring-2 focus:ring-danger-400 focus:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        ${variant !== 'minimal' ? 'shadow-sm hover:shadow-md' : ''}
        ${variantStyles[variant]}
        ${className}
      `}
      aria-label={liked ? 'Unlike this video' : 'Like this video'}
      aria-pressed={liked}
    >
      <HeartbeatAnimation 
        isAnimating={isAnimating} 
        size={size}
      >
        <Heart
          className={`
            ${config.icon}
            transition-all duration-200
            ${liked ? 'fill-current' : 'fill-none'}
            ${isLoading ? 'animate-pulse' : ''}
          `}
        />
      </HeartbeatAnimation>
    </button>
  );
};

export default LikeButton;
